import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Bell, Check, CheckCheck, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { notificationApi } from '@/services/api'
import { Notification } from '@/types'
import { formatDate } from '@/lib/utils'
import { getNotificationText } from '@/lib/notificationTranslations'

interface NotificationDropdownProps {
  isOpen: boolean
  onClose: () => void
}

export function NotificationDropdown({ isOpen, onClose }: NotificationDropdownProps) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const currentLanguage = i18n.language || 'pt'

  // Fetch notifications (only if dropdown is open and user is authenticated)
  const { data: notifications, isLoading } = useQuery(
    ['notifications', currentLanguage],
    async () => {
      try {
        const response = await notificationApi.getNotifications()
        // Handle paginated response from Django REST Framework
        const data = response.data as any
        let allNotifications = []
        if (data?.results && Array.isArray(data.results)) {
          allNotifications = data.results
        } else if (data?.data && Array.isArray(data.data)) {
          allNotifications = data.data
        } else if (Array.isArray(data)) {
          allNotifications = data
        }
        
        // Filter notifications to show only the current language
        // Map language codes: 'pt' -> 'pt', 'en' -> 'en', 'fr' -> 'fr', 'es' -> 'es'
        const filteredNotifications = allNotifications.filter((n: Notification) => {
          const notificationLang = n.language || 'pt'
          // Normalize language codes (handle 'pt-BR', 'en-US', etc.)
          const normalizedLang = notificationLang.split('-')[0].toLowerCase()
          const normalizedCurrent = currentLanguage.split('-')[0].toLowerCase()
          return normalizedLang === normalizedCurrent
        })
        
        return filteredNotifications
      } catch (error) {
        console.error('[NotificationDropdown] Error fetching notifications:', error)
        return []
      }
    },
    { 
      refetchInterval: 30000, // Refetch every 30 seconds
      enabled: isOpen, // Only fetch when dropdown is open
      retry: false // Don't retry on error to avoid loops
    }
  )

  // Fetch unread count (only if dropdown is open)
  const { data: unreadCountData } = useQuery(
    ['unread-count', currentLanguage],
    async () => {
      try {
        const response = await notificationApi.getUnreadCount()
        return response.data?.data || response.data || { unread_count: 0 }
      } catch (error) {
        return { unread_count: 0 }
      }
    },
    { 
      refetchInterval: 10000, // Refetch every 10 seconds
      enabled: isOpen, // Only fetch when dropdown is open
      retry: false // Don't retry on error to avoid loops
    }
  )

  const unreadCount = unreadCountData?.unread_count || 0

  // Mark as read mutation - marks all related notifications (same transaction, different languages)
  const markAsReadMutation = useMutation(
    async (id: string) => {
      // Find the notification being marked as read
      const notification = notifications?.find((n: Notification) => n.id === id)
      
      if (!notification) {
        return await notificationApi.markAsRead(id)
      }
      
      // Find all related notifications (same code and params, but different language)
      const relatedNotifications = notifications?.filter((n: Notification) => {
        // Same code and params (for new format)
        if (notification.code && n.code) {
          return notification.code === n.code &&
                 JSON.stringify(notification.params || {}) === JSON.stringify(n.params || {}) &&
                 // Same timestamp (within 1 minute)
                 Math.abs(new Date(notification.created_at).getTime() - new Date(n.created_at).getTime()) < 60000
        }
        // Same transaction ID (for legacy format)
        if (notification.data?.transaction_id && n.data?.transaction_id) {
          return notification.data.transaction_id === n.data.transaction_id
        }
        // Same amount and currency (for transactions)
        if (notification.data?.amount && notification.data?.currency && 
            n.data?.amount && n.data?.currency) {
          return notification.data.amount === n.data.amount && 
                 notification.data.currency === n.data.currency &&
                 // Same timestamp (within 1 minute)
                 Math.abs(new Date(notification.created_at).getTime() - new Date(n.created_at).getTime()) < 60000
        }
        // Same title (for system notifications)
        if (notification.type === 'system' && n.type === 'system') {
          return notification.title === n.title &&
                 Math.abs(new Date(notification.created_at).getTime() - new Date(n.created_at).getTime()) < 60000
        }
        return false
      }) || []
      
      // Mark all related notifications as read
      const markPromises = relatedNotifications.map((n: Notification) => 
        notificationApi.markAsRead(n.id).catch(() => null) // Don't fail if one fails
      )
      
      // Wait for all to complete and return the first response
      const results = await Promise.all(markPromises)
      return results[0] || await notificationApi.markAsRead(id)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('unread-count')
      },
    }
  )

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation(
    () => notificationApi.markAllAsRead(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('unread-count')
      },
    }
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        // Check if the click is on the notification button (parent)
        const notificationButton = (event.target as HTMLElement)?.closest('button')
        if (!notificationButton?.querySelector('svg')) {
          onClose()
        }
      }
    }

    if (isOpen) {
      // Use setTimeout to avoid immediate closure
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true)
      }, 0)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [isOpen, onClose])

  const handleMarkAsRead = (id: string) => {
    markAsReadMutation.mutate(id)
  }

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate()
  }

  const unreadNotifications = notifications?.filter((n: Notification) => !n.is_read) || []
  const readNotifications = notifications?.filter((n: Notification) => n.is_read) || []

  // Refetch notifications when language changes
  useEffect(() => {
    if (isOpen) {
      queryClient.invalidateQueries('notifications')
    }
  }, [currentLanguage, isOpen, queryClient])

  if (!isOpen) return null

  return (
    <AnimatePresence mode="wait">
      <motion.div
        ref={dropdownRef}
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="fixed sm:absolute top-14 right-2 sm:right-0 sm:top-full sm:mt-2 w-[calc(100vw-1rem)] sm:w-96 max-w-[calc(100vw-1rem)] sm:max-w-none bg-white rounded-lg shadow-xl border border-gray-200 z-[100] max-h-[calc(100vh-5.5rem)] sm:max-h-[600px] flex flex-col"
      >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-text-primary">
              {t('notifications.title') || 'Notificações'}
            </h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={markAllAsReadMutation.isLoading}
                  className="text-xs text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                  title={t('notifications.markAllAsRead') || 'Marcar todas como lidas'}
                >
                  <CheckCheck className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-text-secondary">
                {t('notifications.loading') || 'A carregar...'}
              </div>
            ) : notifications?.length === 0 ? (
              <div className="p-8 text-center text-text-secondary">
                <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">
                  {t('notifications.noNotifications') || 'Nenhuma notificação'}
                </p>
              </div>
            ) : (
              <>
                {/* Unread Notifications */}
                {unreadNotifications.length > 0 && (
                  <div className="p-2">
                    <div className="px-2 py-1 text-xs font-semibold text-text-secondary uppercase">
                      {t('notifications.unread') || 'Não lidas'}
                    </div>
                    {unreadNotifications.map((notification: Notification) => {
                      const { title, message } = getNotificationText(notification, t)
                      return (
                        <motion.div
                          key={notification.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="p-3 bg-primary/5 border-l-4 border-primary rounded-lg mb-2 cursor-pointer hover:bg-primary/10 transition-colors"
                          onClick={() => handleMarkAsRead(notification.id)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="text-sm font-semibold text-text-primary truncate">
                                  {title}
                                </h4>
                                <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                              </div>
                              <p className="text-xs text-text-secondary line-clamp-2 break-words">
                                {message}
                              </p>
                              <p className="text-xs text-text-tertiary mt-1">
                                {formatDate(notification.created_at)}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleMarkAsRead(notification.id)
                              }}
                              className="ml-2 p-1 text-text-secondary hover:text-primary transition-colors flex-shrink-0"
                              title={t('notifications.markAsRead') || 'Marcar como lida'}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                )}

                {/* Read Notifications */}
                {readNotifications.length > 0 && (
                  <div className="p-2 border-t border-gray-200">
                    <div className="px-2 py-1 text-xs font-semibold text-text-secondary uppercase">
                      {t('notifications.read') || 'Lidas'}
                    </div>
                    {readNotifications.map((notification: Notification) => {
                      const { title, message } = getNotificationText(notification, t)
                      return (
                        <motion.div
                          key={notification.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="p-3 rounded-lg mb-2 cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-text-secondary truncate mb-1">
                                {title}
                              </h4>
                              <p className="text-xs text-text-tertiary line-clamp-2 break-words">
                                {message}
                              </p>
                              <p className="text-xs text-text-tertiary mt-1">
                                {formatDate(notification.created_at)}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
  )
}

